/**
 * InlineCardInput — Minimal Stripe CardElement that renders immediately
 * without needing a SetupIntent. The parent calls `confirmCard()` after
 * creating the SetupIntent (once the tenant exists).
 *
 * Usage:
 *   const cardRef = useRef<InlineCardHandle>(null);
 *   <InlineCardInput ref={cardRef} />
 *   // Later: await cardRef.current.confirmCard(clientSecret);
 */

import {
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

export interface InlineCardHandle {
  confirmCard: (clientSecret: string) => Promise<{ success: boolean; error?: string }>;
}

// ---- Inner (runs inside <Elements>) ----

const InnerCardInput = forwardRef<InlineCardHandle>((_, ref) => {
  const stripe = useStripe();
  const elements = useElements();

  useImperativeHandle(ref, () => ({
    confirmCard: async (clientSecret: string) => {
      if (!stripe || !elements) {
        return { success: false, error: 'Card form not ready' };
      }
      const card = elements.getElement(CardElement);
      if (!card) {
        return { success: false, error: 'Card input not found' };
      }
      const { error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (error) {
        return { success: false, error: error.message ?? 'Card verification failed' };
      }
      return { success: true };
    },
  }));

  return (
    <div className="w-full min-h-[20px]">
      <CardElement
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#171D27',
              fontFamily: 'inherit',
              lineHeight: '24px',
              '::placeholder': { color: '#9ca3af' },
            },
            invalid: { color: '#e53e3e' },
          },
          hidePostalCode: true,
        }}
      />
    </div>
  );
});

InnerCardInput.displayName = 'InnerCardInput';

// ---- Outer wrapper (loads Stripe, renders Elements) ----

interface InlineCardInputProps {
  publishableKey: string;
}

export const InlineCardInput = forwardRef<InlineCardHandle, InlineCardInputProps>(
  ({ publishableKey }, ref) => {
    const [stripePromise] = useState(() => loadStripe(publishableKey));

    return (
      <Elements stripe={stripePromise}>
        <InnerCardInput ref={ref} />
      </Elements>
    );
  },
);

InlineCardInput.displayName = 'InlineCardInput';

export default InlineCardInput;
